import type { ChangeEvent, ComponentPropsWithoutRef, ReactNode } from 'react';
import { forwardRef, useMemo, useRef, useState } from 'react';
import { Box, Flex, Input } from '@chakra-ui/react';
import { Button, Popover, Input as KInput } from '@kula-ai/components';
import { Plus, SearchMd, ChevronRight, XClose } from '@kula-ai/icons-core';
import { KText } from '@/ui-library/components/text';
import { useShowToast } from '@/hooks';
import { useFileValidation } from '@/hooks/useFileValidation';
import {
  useAddOfferDocumentMutation,
  useUploadAttachmentMutation,
} from '../../api';
import { useOfferStore } from '../../store';
import {
  ADD_DOCUMENT_ERROR_COPY,
  addDocumentErrorCode,
  addDocumentErrorCopy,
} from './addDocumentErrors';

// First-class mode: added docs become offer documents, so the cap mirrors the BE's
// AtsOfferTemplate::MAX_DOCUMENTS (10 documents per offer template).
const MAX_DOCUMENTS = 10;
// Legacy attachment mode (multi-doc flag off): raw additional-files caps (CORE-4807).
const MAX_FILES = 10;
const MAX_TOTAL_SIZE_MB = 15;
const BYTES_PER_MB = 1024 * 1024;

const attachmentBytes = (a: { byte_size?: number; size?: number }) =>
  a?.byte_size ?? a?.size ?? 0;

// A document already on the offer (an offer_documents[] entry, or a legacy attachment).
interface OfferExistingDoc {
  filename?: string;
  byte_size?: number;
}

export interface OfferTemplateDoc {
  // Stored .docx URL, fetched and re-uploaded as a first-class offer document.
  url?: string;
  filename?: string;
  // With the filename this reliably detects the doc already being on the offer.
  byteSize?: number;
}

export interface OfferTemplateOption {
  id: number | string;
  name: string;
  // Every document of the template (multi-doc templates carry several) — picking
  // the template attaches all of them.
  documents?: OfferTemplateDoc[];
}

const DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

// Only .docx can become a token-bearing offer document; everything else (PDF,
// images) is stored as a plain attachment — no tokens, no substitution.
const isDocxFile = (file: File) =>
  file.name.toLowerCase().endsWith('.docx') || file.type === DOCX_MIME;

const docFilename = (doc: OfferTemplateDoc, templateName: string) =>
  doc.filename || `${templateName}.docx`;

// Whether one template document matches one offer document — by filename,
// tightened by byte_size when BOTH sides report one.
const docMatches = (doc: OfferTemplateDoc, offerDoc: OfferExistingDoc) => {
  if (offerDoc?.filename !== doc.filename) return false;
  if (
    typeof offerDoc?.byte_size === 'number' &&
    typeof doc.byteSize === 'number'
  ) {
    return offerDoc.byte_size === doc.byteSize;
  }
  return true;
};

// The template's documents that are NOT yet on the offer, matched as a MULTISET
// (each offer doc satisfies at most one template doc). Filename-only matching
// would double-count when a template carries several same-named docs, so a
// template with two "Adobe_offer.docx" would wrongly read as fully present after
// one is deleted. Consuming matches makes the deleted one count as missing.
const missingDocs = (
  docs: OfferTemplateDoc[],
  existingDocs: OfferExistingDoc[]
): OfferTemplateDoc[] => {
  const pool = [...existingDocs];
  const missing: OfferTemplateDoc[] = [];
  for (const doc of docs) {
    const idx = pool.findIndex((a) => docMatches(doc, a));
    if (idx === -1) missing.push(doc);
    else pool.splice(idx, 1);
  }
  return missing;
};

// One dropdown row. Matches Figma 2350-8778: 44px tall, 10x12 padding, a 1px
// secondary-100 (#E9EBF4) bottom divider, secondary-25 (#F8F9FC) hover, and
// secondary-700 (#6D659D) regular 14/20 label. forwardRef so it can be a
// Popover trigger (Radix asChild needs the ref).
interface RowOwnProps {
  children: ReactNode;
  onClick?: () => void;
  rightIcon?: ReactNode;
  divider?: boolean;
  disabled?: boolean;
}
// `rest` carries the props Radix injects when Row is used as a Popover trigger
// (asChild) — onPointerEnter/onFocus/data-state/aria-*. They MUST reach the DOM
// button or the submenu never opens.
type RowProps = RowOwnProps &
  Omit<ComponentPropsWithoutRef<'button'>, keyof RowOwnProps>;
const Row = forwardRef<HTMLButtonElement, RowProps>(
  (
    {
      children,
      onClick,
      rightIcon,
      divider = false,
      disabled = false,
      ...rest
    },
    ref
  ) => (
    <Flex
      ref={ref}
      as="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      {...rest}
      align="center"
      gap="12px"
      w="100%"
      h="44px"
      px="12px"
      bg="white"
      _hover={{ bg: disabled ? 'white' : 'secondary-25' }}
      borderBottom={divider ? '1px solid' : 'none'}
      borderColor="secondary-100"
      cursor={disabled ? 'default' : 'pointer'}
      textAlign="left"
    >
      <Box flex="1" minW="0">
        <KText size="sm-regular" color="secondary-700" isTruncated>
          {children}
        </KText>
      </Box>
      {rightIcon}
    </Flex>
  )
);
Row.displayName = 'AddMoreFilesRow';

// Clicking "Add more files" opens a dropdown (Figma 2350-8778): "Upload new
// document" (device upload) and "Add from existing template" — a click-opened
// submenu with a search box + the org's offer-letter templates. Picking a
// template fetches its .docx and appends it through the add_document flow so it
// becomes a first-class offer document (token-validated + substituted on
// generate) exactly like the selected template's docs, then closes the menu.
// Only .docx is accepted (offer documents carry tokens). Templates whose
// document is already on the offer are hidden so they can't be added twice.
export const AddMoreFilesButton = ({
  offerId,
  variant = 'tertiary',
  templates = [],
  existingDocs = [],
  onDocumentAdded,
}: {
  offerId?: number | string;
  variant?: string;
  templates?: OfferTemplateOption[];
  // Documents already on the offer (offer_documents[]) — drives the cap + hiding.
  existingDocs?: OfferExistingDoc[];
  // Applies the per-offer template the add_document response returns.
  onDocumentAdded?: (_template: unknown) => void | Promise<void>;
}) => {
  const storeOfferId = useOfferStore((state) => state.offerId);
  const addOfferAttachment = useOfferStore((state) => state.addOfferAttachment);
  const offerAttachments = useOfferStore((state) => state.offerAttachments);
  const toast = useShowToast();
  const { handleAttachmentValidation } = useFileValidation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [templateQuery, setTemplateQuery] = useState('');
  // Templates picked this session — hidden immediately so they can't be added
  // twice even before the add round-trips.
  const [pickedIds, setPickedIds] = useState<Set<number | string>>(new Set());

  // First-class mode (multi-doc flag on): added files become offer documents,
  // token-validated + substituted like the selected template's docs. Legacy mode
  // (flag off, no onDocumentAdded): raw attachments, as before.
  const firstClass = !!onDocumentAdded;
  const existing: OfferExistingDoc[] = firstClass
    ? existingDocs
    : offerAttachments;

  const addMutation = useAddOfferDocumentMutation({
    // The mutation polls the async append, so this fires on the real outcome and carries an err_*
    // code — map it rather than surfacing the code itself.
    onError: (error: { message?: string }) => {
      toast({
        title: 'Failed to add document',
        description: addDocumentErrorCopy(error),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const uploadMutation = useUploadAttachmentMutation({
    onSuccess: (data: { data?: { attachments?: unknown[] } }) => {
      const attachment = data?.data?.attachments?.[0];
      if (attachment) addOfferAttachment(attachment);
    },
    onError: (error: { message?: string }) => {
      toast({
        title: 'Failed to upload file',
        description: error?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // First-class caps by type: .docx count toward the offer-document limit,
  // everything else toward the attachment limit (+ combined size).
  const overAttachmentSize = (newFiles: File[]) => {
    const existingBytes = offerAttachments.reduce(
      (total, a) => total + attachmentBytes(a),
      0
    );
    const newBytes = newFiles.reduce((total, f) => total + f.size, 0);
    return (existingBytes + newBytes) / BYTES_PER_MB > MAX_TOTAL_SIZE_MB;
  };
  const maxCount = firstClass ? MAX_DOCUMENTS : MAX_FILES;
  const capLabel = firstClass
    ? `You can add up to ${MAX_DOCUMENTS} documents`
    : `You can add up to ${MAX_FILES} files`;

  const handleChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0) return;

    // Reject the whole selection if any file is damaged / unsupported / empty.
    const isValid = await handleAttachmentValidation({ files });
    if (!isValid) return;

    const id = offerId || storeOfferId || 0;

    if (!firstClass) {
      // Legacy attachment path: cap count + combined size, one upload per file.
      if (offerAttachments.length + files.length > MAX_FILES) {
        toast({
          title: `You can add up to ${MAX_FILES} files`,
          status: 'error',
        });
        return;
      }
      if (overAttachmentSize(files)) {
        toast({
          title: 'Total file size exceeds limit',
          description: `All additional files together must be under ${MAX_TOTAL_SIZE_MB} MB.`,
          status: 'error',
        });
        return;
      }
      files.forEach((file) => uploadMutation.mutate({ id, file }));
      return;
    }

    // First-class: .docx become token-bearing offer documents (add_document);
    // PDFs and other files can't be token-filled, so they're stored as plain
    // attachments (upload_attachment) — same static rendering as before.
    const docxFiles = files.filter(isDocxFile);
    const otherFiles = files.filter((f) => !isDocxFile(f));

    if (
      docxFiles.length &&
      existingDocs.length + docxFiles.length > MAX_DOCUMENTS
    ) {
      toast({
        title: `You can add up to ${MAX_DOCUMENTS} documents`,
        status: 'error',
      });
      return;
    }
    if (otherFiles.length) {
      if (offerAttachments.length + otherFiles.length > MAX_FILES) {
        toast({
          title: `You can add up to ${MAX_FILES} files`,
          status: 'error',
        });
        return;
      }
      if (overAttachmentSize(otherFiles)) {
        toast({
          title: 'Total file size exceeds limit',
          description: `All attached files together must be under ${MAX_TOTAL_SIZE_MB} MB.`,
          status: 'error',
        });
        return;
      }
    }

    try {
      // Append all .docx in one request → a single clone / reset / envelope-void and one
      // wholesale MAX_DOCUMENTS check. Attachments upload in parallel.
      if (docxFiles.length) {
        const template = await addMutation.mutateAsync({
          id,
          files: docxFiles,
        });
        await onDocumentAdded?.(template);
      }
      otherFiles.forEach((file) => uploadMutation.mutate({ id, file }));
    } catch {
      // error already surfaced by the mutation's onError.
    }
  };

  const closeMenu = () => {
    setMenuOpen(false);
    setTemplateQuery('');
  };

  const unpick = (id: number | string) =>
    setPickedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  // Pick a template: hide it + close the menu immediately, then append EVERY one
  // of its documents via add_document (multi-doc templates carry several) so each
  // becomes a first-class offer document. The optimistic hide is cleared once
  // added so the real offer-document state takes over — deleting a document
  // un-hides the template.
  const pickTemplate = async (template: OfferTemplateOption) => {
    // Only the documents not already on the offer (multiset — so a deleted doc
    // gets re-added even when the template has several same-named docs).
    const docs = missingDocs(
      (template.documents ?? []).filter((d) => d.url),
      existing
    );
    if (docs.length === 0) {
      toast({
        title: 'This template has no document to add',
        status: 'error',
      });
      return;
    }
    if (existing.length + docs.length > maxCount) {
      toast({ title: capLabel, status: 'error' });
      return;
    }
    setPickedIds((prev) => new Set(prev).add(template.id));
    closeMenu();
    const id = offerId || storeOfferId || 0;
    try {
      // Fetch each stored .docx, then append the whole set in one request (first-class)
      // so a multi-doc template is one clone/reset, not N.
      const files: File[] = [];
      for (const doc of docs) {
        const res = await fetch(doc.url as string);
        if (!res.ok) throw new Error('fetch failed');
        const blob = await res.blob();
        files.push(
          new File([blob], docFilename(doc, template.name), {
            type: blob.type || DOCX_MIME,
          })
        );
      }
      if (firstClass) {
        const updated = await addMutation.mutateAsync({ id, files });
        await onDocumentAdded?.(updated);
      } else {
        for (const file of files) {
          await uploadMutation.mutateAsync({ id, file });
        }
      }
    } catch (error) {
      // Both mutations already surface their own failure via onError, so re-toasting a known
      // err_* code here would double up. What is left for this handler is the document fetch
      // above — and anything unforeseen.
      const code = addDocumentErrorCode(
        error as Parameters<typeof addDocumentErrorCode>[0]
      );
      if (!code || !(code in ADD_DOCUMENT_ERROR_COPY)) {
        toast({ title: 'Failed to add template', status: 'error' });
      }
    } finally {
      unpick(template.id);
    }
  };

  // Hide a template if it was picked this session (optimistic, until added), or
  // ALL of its documents are already on the offer (multiset). A multi-doc
  // template reappears as soon as any of its documents is missing (e.g. deleted).
  const availableTemplates = useMemo(
    () =>
      templates.filter((t) => {
        if (pickedIds.has(t.id)) return false;
        const docs = (t.documents ?? []).filter((d) => d.filename);
        if (docs.length > 0 && missingDocs(docs, existing).length === 0) {
          return false;
        }
        return true;
      }),
    [templates, pickedIds, existing]
  );

  const normalizedQuery = (templateQuery ?? '').trim().toLowerCase();
  const filteredTemplates = normalizedQuery
    ? availableTemplates.filter((t) =>
        t.name?.toLowerCase().includes(normalizedQuery)
      )
    : availableTemplates;

  return (
    <Box position="relative" w="186px">
      {/* Hidden input, opened programmatically from "Upload new document". Both
          modes accept the same types; in first-class mode .docx become
          token-bearing offer documents and the rest are plain attachments. */}
      <Input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleChange}
        display="none"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
      />
      <Popover
        open={menuOpen}
        onOpenChange={(open) => (open ? setMenuOpen(true) : closeMenu())}
        align="start"
        side="bottom"
        // p-0 so the full-bleed rows/dividers reach the panel edges; the panel
        // keeps its default border/rounded/shadow (= the Figma card).
        className="p-0 overflow-hidden"
        trigger={
          <Button
            variant={variant}
            size="regular"
            isLoading={addMutation.isPending || uploadMutation.isPending}
            leftElement={<Plus size={16} />}
            style={{ width: '100%' }}
          >
            Add more files
          </Button>
        }
      >
        <Box w="244px">
          <Row
            divider={templates.length > 0}
            onClick={() => {
              closeMenu();
              fileInputRef.current?.click();
            }}
          >
            Upload new document
          </Row>

          {templates.length > 0 && (
            <Popover
              // Opens on click (not hover) — clicking the row reveals the search
              // + template list to the right, with a 6px gap.
              side="right"
              align="start"
              sideOffset={6}
              className="p-0 overflow-hidden"
              trigger={
                <Row
                  rightIcon={
                    <Box as="span" display="flex" color="secondary-700">
                      <ChevronRight size={18} />
                    </Box>
                  }
                >
                  Add from existing template
                </Row>
              }
            >
              <Box w="320px">
                {/* Search section: 12px padding + bottom divider (Figma). */}
                <Box
                  p="12px"
                  borderBottom="1px solid"
                  borderColor="secondary-100"
                >
                  <KInput
                    autoFocus
                    size="sm"
                    value={templateQuery}
                    placeholder="Search templates"
                    leftElement={<SearchMd size={16} />}
                    // Own clear button (not kula-ai `clearable`, which doesn't
                    // sync with a controlled value) so ✕ resets text + filter.
                    rightElement={
                      templateQuery ? (
                        <Box
                          as="button"
                          type="button"
                          display="flex"
                          alignItems="center"
                          color="secondary-500"
                          aria-label="Clear search"
                          onClick={() => setTemplateQuery('')}
                        >
                          <XClose size={16} />
                        </Box>
                      ) : undefined
                    }
                    onChange={(e) => setTemplateQuery(e.target.value)}
                  />
                </Box>
                <Box maxH="264px" overflowY="auto">
                  {filteredTemplates.length ? (
                    filteredTemplates.map((template, index) => (
                      <Row
                        key={template.id}
                        divider={index < filteredTemplates.length - 1}
                        onClick={() => pickTemplate(template)}
                      >
                        {template.name}
                      </Row>
                    ))
                  ) : (
                    <Row disabled>
                      {normalizedQuery
                        ? 'No matching templates'
                        : 'No templates available'}
                    </Row>
                  )}
                </Box>
              </Box>
            </Popover>
          )}
        </Box>
      </Popover>
    </Box>
  );
};
